<script lang="ts">
  // Scenarios Index sub-panel per INTERFACE-SPECIFICATION.md §10.7.
  //
  // Lists the test scenarios from tests/scenarios/. Each scenario
  // links to its YAML in the GitHub repo for inspection. CI run
  // status surfaces via the validate.yml workflow.

  interface Scenario {
    id: string;
    label: string;
    description: string;
    arch_section?: string;
  }

  const scenarios: Scenario[] = [
    { id: '01', label: 'Single-rate-change lifecycle', description: 'A rate edit moves through Proposed → Under Review → Approved → In Effect → Superseded.', arch_section: 'GOVERNANCE-ARCHITECTURE.md §3' },
    { id: '02', label: 'Queued multi-version time-gate', description: 'Two approved versions queued; the Worker serves each at its effective-from instant.', arch_section: '§5.4' },
    { id: '03', label: 'Partial rollback', description: 'A subset of cube cells reverts to a prior version while other cells stay current.', arch_section: '§9.4' },
    { id: '04', label: 'Concurrent non-collision', description: 'Two users edit disjoint cells in parallel drafts; both proposals merge cleanly.', arch_section: '§3.4.4' },
    { id: '05', label: 'Concurrent collision and reconcile', description: 'Two users edit the same cell; the second user reconciles via the conflict UI.', arch_section: '§3.4.4' },
    { id: '06', label: 'Authorization denial', description: 'A proposer without the required role attempts to submit; the proxy returns 403.', arch_section: '§7.5' },
    { id: '07', label: 'DST transition effective-from', description: 'Non-existent and ambiguous effective-from instants surface in the picker.', arch_section: '§5.5' },
    { id: '08', label: 'Symmetry-mutation detection', description: 'An edit that would silently change a declared asymmetry is flagged by the symmetry checker.', arch_section: '§10' }
  ];

  const repoBaseUrl = 'https://github.com/netevo-accounts/harmoney-calculator-source-of-truth-dev/blob/main/tests/scenarios/';

  function urlFor(s: Scenario): string {
    const padded = s.id.padStart(2, '0');
    return `${repoBaseUrl}${padded}-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.yaml`;
  }
</script>

<section>
  <h3 class="section-heading">Scenarios index</h3>
  <p class="muted">Executable governance proofs in <code>tests/scenarios/</code>. Each scenario file is a given/when/then YAML; the harness exercises the catalogue-client end-to-end against the fake GitHub backend.</p>
  <ul class="scenarios-list">
    {#each scenarios as s (s.id)}
      <li class="scenario-card">
        <header>
          <strong>{s.id} · {s.label}</strong>
          <span class="muted">{s.arch_section}</span>
        </header>
        <p>{s.description}</p>
        <a href={urlFor(s)} target="_blank" rel="noopener" data-external>View YAML on GitHub →</a>
      </li>
    {/each}
  </ul>
</section>

<style>
  .scenarios-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: var(--space-3); }
  .scenario-card {
    background: var(--surface-card);
    border: 1px solid var(--surface-line-soft);
    border-radius: var(--radius-md);
    padding: var(--space-3);
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
  .scenario-card header { display: flex; justify-content: space-between; gap: var(--space-3); flex-wrap: wrap; }
  code { font-family: var(--font-mono); font-size: 0.9em; }
</style>
